export type StatementPrintPermissions = {
  canView: boolean;
  canPrepare: boolean;
  canPrintLatest: boolean;
  canPrintHistorical: boolean;
  canCreatePackage: boolean;
  canOverrideAddress: boolean;
  canConfirmOutcome: boolean;
  canViewHistory: boolean;
  canViewAudit: boolean;
  canExportReport: boolean;
};

export type StatementPrintQueueItem = {
  companyId: string;
  clientId?: string | null;
  customerId: string;
  accountId: string;
  statementId: string;
  statementNumber: string;
  statementDate?: string | null;
  balanceCents: number;
  lifecycleStatus: string;
  customerName: string;
  customerNumber: string;
  clientCompany: string;
  recipientName: string;
  recipientEmail: string;
  recipientStatus: string;
  documentVersionId?: string | null;
  versionNumber?: number | null;
  documentStatus: string;
  isActive: boolean;
  mimeType?: string | null;
  fileSize?: number | null;
  generatedAt?: string | null;
  storageProvider?: string | null;
  storageKeyPresent: boolean;
  addressReadiness: string;
  integrityStatus: string;
  lastPrintedAt?: string | null;
  printCount: number;
  printStatus: string;
};

export type StatementPrintVersion = {
  documentVersionId: string;
  versionNumber: number;
  documentStatus: string;
  isActive: boolean;
  contentHash?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  storageProvider?: string | null;
  storageKeyPresent: boolean;
  generatedBy?: string | null;
  generatedAt?: string | null;
  createdAt: string;
  correctionReason?: string | null;
};

export type StatementPrintPreparation = {
  preparationId: string;
  statementId: string;
  accountId: string;
  documentVersionId: string;
  versionNumber: number;
  sourceWorkspace: string;
  actorName?: string | null;
  actorRole: string;
  status: string;
  copies: number;
  paperSize: string;
  orientation: string;
  colorPreference: string;
  sidesPreference: string;
  includeCoverSheet: boolean;
  includeAddressPage: boolean;
  includeControlPage: boolean;
  addressSource?: string | null;
  address: Record<string, unknown>;
  addressReady: boolean;
  packageId?: string | null;
  packageChecksum?: string | null;
  packagePageCount?: number | null;
  packageExpiresAt?: string | null;
  integrityChecksum?: string | null;
  integrityVerifiedAt?: string | null;
  dialogOpenedAt?: string | null;
  outcome?: string | null;
  outcomeReason?: string | null;
  priorPrintPreparationId?: string | null;
  correlationId: string;
  createdAt: string;
  updatedAt: string;
};

export type StatementPrintDetail = {
  statement: {
    statementId: string;
    statementNumber: string;
    statementDate?: string | null;
    customerName: string;
    customerNumber: string;
    accountId: string;
    clientCompany?: string | null;
    balanceCents: number;
    lifecycleStatus: string;
    recipientName?: string | null;
    recipientEmail?: string | null;
    placementId?: string | null;
  };
  versions: StatementPrintVersion[];
  history: StatementPrintPreparation[];
  audit: Array<{
    auditId: string;
    printPreparationId?: string | null;
    documentVersionId?: string | null;
    versionNumber?: number | null;
    eventType: string;
    resultStatus: string;
    sourceWorkspace: string;
    actorName?: string | null;
    actorRole: string;
    correlationId: string;
    metadata: Record<string, unknown>;
    errorCode?: string | null;
    safeErrorMessage?: string | null;
    occurredAt: string;
  }>;
  permissions: StatementPrintPermissions;
  address: {
    name: string;
    company: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    region: string;
    postalCode: string;
    country: string;
    source: string;
    ready: boolean;
    validationStatus: string;
    missingFields: string[];
  };
  temporaryArtifactPolicy: {
    canonicalSourceRemainsImmutable: boolean;
    packageTtlHours: number;
    cleanupStatus: string;
  };
};

export type StatementPrintOptions = {
  copies: number;
  paperSize: string;
  orientation: string;
  colorPreference: string;
  sidesPreference: string;
  includeCoverSheet: boolean;
  includeAddressPage: boolean;
  includeControlPage: boolean;
  manualAddress?: Record<string, unknown> | null;
  reprintReason?: string;
  priorPrintPreparationId?: string;
};

async function fetchJson<T>(url: string, init?: RequestInit) {
  const response = await fetch(url, { credentials: "include", ...init, headers: { Accept: "application/json", ...(init?.headers || {}) } });
  if (!response.ok) throw new Error((await response.text()) || "Statement Print request failed.");
  return response.json() as Promise<T>;
}

export async function listStatementPrintQueue(options: Record<string, string | number | undefined>) {
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  return fetchJson<{
    items: StatementPrintQueueItem[];
    permissions: StatementPrintPermissions;
    pagination: { page: number; pageSize: number; total: number; totalPages: number; hasNext: boolean; hasPrevious: boolean };
  }>(`/api/ar-recovery/statements/prints?${params.toString()}`);
}

export async function getStatementPrintDetail(statementId: string) {
  return fetchJson<StatementPrintDetail>(`/api/ar-recovery/statements/${encodeURIComponent(statementId)}/prints`);
}

export async function prepareStatementPrint(statementId: string, versionId: string, options: StatementPrintOptions, sourceWorkspace: string) {
  const correlationId = crypto.randomUUID();
  return fetchJson<{ preparation: StatementPrintPreparation }>(
    `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/prints`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        versionId,
        options,
        sourceWorkspace,
        correlationId,
        idempotencyKey: `statement-print:${statementId}:${versionId}:${correlationId}`,
      }),
    },
  );
}

export async function recordStatementPrintDialog(statementId: string, preparationId: string) {
  return fetchJson<{ preparation: StatementPrintPreparation }>(
    `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/prints/${encodeURIComponent(preparationId)}/dialog`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
  );
}

export function statementPrintPdfUrl(statementId: string, preparationId: string) {
  return `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/prints/${encodeURIComponent(preparationId)}/pdf`;
}

export async function confirmStatementPrintOutcome(statementId: string, preparationId: string, outcome: string, reason: string) {
  return fetchJson<{ preparation: StatementPrintPreparation }>(
    `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/prints/${encodeURIComponent(preparationId)}/outcome`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ outcome, reason }),
    },
  );
}

export async function getStatementPrintReports() {
  return fetchJson<{ reports: {
    summary: { confirmed: number; cancelled: number; failed: number; reprints: number; historicalVersionPrints: number };
    records: Array<Record<string, unknown>>;
  } }>("/api/ar-recovery/statements/prints?view=reports");
}

export async function downloadStatementPrintReport() {
  const response = await fetch("/api/ar-recovery/statements/prints?view=export", { credentials: "include" });
  if (!response.ok) throw new Error((await response.text()) || "Print report export failed.");
  const disposition = response.headers.get("Content-Disposition") || "";
  return {
    blob: await response.blob(),
    fileName: disposition.match(/filename="([^"]+)"/i)?.[1] || "statement-print-report.csv",
  };
}
